/**
 * Product Grid Carousel
 * Swiper-powered image carousel for product grid tiles.
 *
 * Desktop (hover-capable, fine pointer) devices: Swiper is only initialized
 * the first time a shopper hovers/focuses the tile, since its arrows and
 * pagination bars stay hidden until then anyway (see product-card-carousel.css).
 * Touch (coarse pointer) devices: Swiper initializes immediately, since the
 * pagination bars are visible by default there and there's no hover event
 * to key a lazy init off of.
 */
class ProductGridCarousel {
  constructor() {
    this.swipers = new Map();
    this.isDesktopPointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.init();
    this.setupMutationObserver();
  }

  init() {
    document.querySelectorAll('[data-product-carousel]').forEach((carousel) => this.watch(carousel));
  }

  watch(carousel) {
    if (this.swipers.has(carousel)) {
      return;
    }

    if (this.isDesktopPointer) {
      const start = () => this.initializeSwiper(carousel);
      carousel.addEventListener('mouseenter', start, { once: true });
      carousel.addEventListener('focusin', start, { once: true });
    } else {
      this.initializeSwiper(carousel);
    }
  }

  initializeSwiper(element) {
    if (this.swipers.has(element)) {
      return this.swipers.get(element);
    }

    const swiper = new Swiper(element, {
      loop: true,
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 300,
      touch: true,
      grabCursor: true,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      pagination: {
        el: element.querySelector('.grid-product__carousel-pagination'),
        type: 'bullets',
        clickable: true,
      },
      navigation: {
        nextEl: element.querySelector('.swiper-button-next'),
        prevEl: element.querySelector('.swiper-button-prev'),
      },
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous image',
        nextSlideMessage: 'Next image',
        paginationBulletMessage: 'Go to image {{index}}',
      },
    });

    this.swipers.set(element, swiper);
    element.addEventListener('keydown', (event) => this.handleKeyboard(event, swiper));

    if (this.isDesktopPointer && element.dataset.carouselHoverSecondary === 'true') {
      this.setupHoverSecondaryImage(element, swiper);
      // On desktop this only ever runs because the user is already hovering
      // (that's what triggered the lazy init), so jump straight to the
      // secondary image instead of waiting for a future mouseenter.
      swiper.slideToLoop(1);
    }

    return swiper;
  }

  /**
   * Mirrors the theme's standard "hover to reveal second image" setting:
   * hovering the card on desktop advances to the second image, while the
   * carousel's own arrows/pagination remain available to browse the rest.
   */
  setupHoverSecondaryImage(element, swiper) {
    const figure = element.closest('.grid-product__content') || element;

    figure.addEventListener('mouseenter', () => {
      if (swiper.realIndex === 0) {
        swiper.slideToLoop(1);
      }
    });

    figure.addEventListener('mouseleave', () => {
      swiper.slideToLoop(0);
    });
  }

  handleKeyboard(event, swiper) {
    if (event.key === 'ArrowLeft') {
      swiper.slidePrev();
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      swiper.slideNext();
      event.preventDefault();
    }
  }

  /**
   * Collection grids can re-render their contents client-side (filtering,
   * sorting, infinite scroll) so newly added carousels need to be picked up
   * without a full page reload.
   */
  setupMutationObserver() {
    const observer = new MutationObserver(() => {
      document.querySelectorAll('[data-product-carousel]').forEach((carousel) => this.watch(carousel));
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductGridCarousel();
  });
} else {
  new ProductGridCarousel();
}
